// 03 — They're here. One name field per chick. This is the last screen that asks
// anything: `actions.startNewGame` writes the save, and the router resets onto
// the brooder, because from there the player is in the game and only setting
// things up (REFOCUS §5).

import type { Breed, HousingType } from '../../core/types';
import { t } from '../../i18n';
import { chickenReferencePath } from '../../render/spriteManifest';
import { startNewGame, type ChickPurchase } from '../actions';
import { button, div, h, link } from '../dom';
import { selectBreedName } from '../selectors';
import type { Screen, ScreenContext } from '../router';
import { brooderSetupScreen } from './brooderSetup';

interface ChickSlot {
  breed: Breed;
  /** Default name from the draft, so a kid only has to fix what they dislike. */
  suggested: string;
}

function readSlots(params: ScreenContext<HTMLElement>['params']): ChickSlot[] {
  const slots: ChickSlot[] = [];
  if (!params.counts) return slots;
  let counts: Record<string, number> = {};
  try {
    counts = JSON.parse(params.counts) as Record<string, number>;
  } catch {
    return slots;
  }
  let index = 0;
  for (const [breed, count] of Object.entries(counts)) {
    for (let i = 0; i < count; i++) {
      index++;
      slots.push({
        breed: breed as Breed,
        suggested: t('onboard.defaultChickName', { n: String(index) }),
      });
    }
  }
  return slots;
}

export function chicksArriveScreen(): Screen<HTMLElement> {
  return {
    id: 'chicksArrive',
    render: (ctx: ScreenContext<HTMLElement>) => {
      const housing = (ctx.params.housing as HousingType) ?? 'backyard';
      const slots = readSlots(ctx.params);
      const names: string[] = slots.map((slot) => slot.suggested);

      const box = div({ class: 'cf-chickbox' });
      slots.forEach((_slot, index) => {
        const size = index === Math.floor(slots.length / 2) ? 'cf-chickbox__img--tall' : '';
        box.appendChild(
          h('img', {
            class: `cf-chickbox__img ${size}`,
            attrs: { src: chickSprite, alt: t('onboard.chickAlt') },
          }),
        );
      });

      const fields = div({ class: 'cf-stack' });
      slots.forEach((slot, index) => {
        const input = h('input', {
          class: 'cf-input',
          attrs: {
            type: 'text',
            value: names[index] ?? '',
            maxlength: '24',
            placeholder: t('onboard.nameHer'),
            'aria-label': t('onboard.nameLabel', { breed: selectBreedName(slot.breed) }),
          },
          on: {
            input: (event) => {
              names[index] = (event.target as HTMLInputElement).value;
            },
          },
        });
        fields.appendChild(
          h(
            'label',
            { class: 'cf-field' },
            h('span', { class: 'cf-field__label', text: selectBreedName(slot.breed) }),
            input,
          ),
        );
      });

      const purchases: ChickPurchase[] = [];
      for (const [breed, count] of Object.entries(countsOf(slots))) {
        purchases.push({ breed: breed as Breed, count });
      }

      const go = button({
        class: 'cf-btn cf-btn--primary',
        text: t('onboard.setUpBrooder'),
        on: {
          click: () => {
            startNewGame(ctx.services.store, {
              housing,
              purchases,
              sexed: ctx.params.sexed === '1',
              veteran: ctx.params.veteran === '1',
              names,
            });
            ctx.router.reset(brooderSetupScreen());
          },
        },
      });

      const screen = div(
        { class: 'cf-screen' },
        div(
          { class: 'cf-screen__head' },
          link({
            class: 'cf-btn cf-btn--link',
            text: t('common.back'),
            on: { click: () => ctx.router.back() },
          }),
          h('span', { class: 'cf-eyebrow', text: t('onboard.step', { n: '3', of: '5' }) }),
        ),
        h('h1', { class: 'cf-display', text: t('onboard.arriveTitle') }),
        h('p', { class: 'cf-sub', text: t('onboard.arriveSubtitle', { count: slots.length }) }),
        box,
        fields,
        div({ class: 'cf-screen__foot' }, go),
      );
      screen.tabIndex = -1;
      return screen;
    },
  };
}

function countsOf(slots: ChickSlot[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const slot of slots) counts[slot.breed] = (counts[slot.breed] ?? 0) + 1;
  return counts;
}

const chickSprite = chickenReferencePath('silkie', true, 'hen');
